import { z } from 'zod'
import { type Crop } from '../../../utils/cropUtils.types'
import { EVENT_NAMES } from '../../../services/Analytics/Analytics'
import type { TILE_SIZES } from '@mixtiles/web-backend-shared'
import type { WarningConfig } from '../../../types/warning.types'

export const TileWarningTypeSchema = z.enum([
  'lowres',
  'crop',
  'change-size',
  'art-tile-orientation',
  'change-size-gallery-wall',
  'incomplete-collage',
  'ai-tile-generating',
])

export type TileWarningType = z.infer<typeof TileWarningTypeSchema>

export type TileWarning =
  | { type: 'lowres' }
  | { type: 'crop'; payload: Crop }
  | {
      type: 'change-size-gallery-wall'
      payload:
        | Crop
        | {
            size: TILE_SIZES
            crop?: { rect: Crop; targetAspectRatio: number }
          }
    }
  | {
      type: 'change-size'
      payload: {
        size: TILE_SIZES
        crop?: { rect: Crop; targetAspectRatio: number }
      }
    }
  | {
      type: 'art-tile-orientation'
      payload: {
        size: TILE_SIZES
      }
    }
  | { type: 'incomplete-collage' }
  | { type: 'ai-tile-generating' }

export const warningConfig: WarningConfig<TileWarningType> = {
  lowres: {
    t: {
      label: 'order.low_quality_warning.warning.label',
      cta: 'order.low_quality_warning.warning.cta',
    },
    analytics: {
      displayed: EVENT_NAMES.LOW_RESOLUTION_WARNING_DISPLAYED,
      tapped: EVENT_NAMES.LOW_RESOLUTION_WARNING_TAPPED,
      dismissed: EVENT_NAMES.TILE_WARNING_DISMISSED,
    },
  },
  crop: {
    t: {
      label: 'order.faces_cropped_warning.label',
      cta: 'order.faces_cropped_warning.cta.crop',
    },
    analytics: {
      displayed: EVENT_NAMES.FACES_CROP_WARNING_DISPLAYED,
      tapped: EVENT_NAMES.FACES_CROP_WARNING_TAPPED,
      dismissed: EVENT_NAMES.TILE_WARNING_DISMISSED,
    },
  },
  'change-size': {
    t: {
      label: 'order.faces_cropped_warning.label',
      cta: 'order.faces_cropped_warning.cta.change_size',
    },
    analytics: {
      displayed: EVENT_NAMES.FACES_CROP_WARNING_DISPLAYED,
      tapped: EVENT_NAMES.FACES_CROP_WARNING_TAPPED,
      dismissed: EVENT_NAMES.TILE_WARNING_DISMISSED,
    },
  },
  'change-size-gallery-wall': {
    t: {
      label: 'order.faces_cropped_warning.label',
      cta: 'order.low_quality_warning.warning.cta',
    },
    analytics: {
      displayed: EVENT_NAMES.FACES_CROP_WARNING_DISPLAYED,
      tapped: EVENT_NAMES.FACES_CROP_WARNING_TAPPED,
      dismissed: EVENT_NAMES.TILE_WARNING_DISMISSED,
    },
  },
  'art-tile-orientation': {
    t: {
      label: 'order.art_orientation_warning.checkout.label.singular',
      cta: 'order.faces_cropped_warning.cta.change_size',
    },
    analytics: {
      displayed: EVENT_NAMES.ART_ORIENTATION_WARNING_DISPLAYED,
      tapped: EVENT_NAMES.ART_ORIENTATION_WARNING_TAPPED,
      dismissed: EVENT_NAMES.TILE_WARNING_DISMISSED,
    },
  },
  'incomplete-collage': {
    t: {
      label: 'order.incomplete_collage_warning.label',
      cta: 'order.add_photos',
    },
    analytics: {
      displayed: EVENT_NAMES.INCOMPLETE_COLLAGE_WARNING_DISPLAYED,
      tapped: EVENT_NAMES.INCOMPLETE_COLLAGE_WARNING_TAPPED,
      dismissed: EVENT_NAMES.TILE_WARNING_DISMISSED,
    },
  },
  'ai-tile-generating': {
    t: {
      label: 'order.ai_tile_generating_warning.label',
      cta: 'order.ai_tile_generating_warning.cta',
    },
    analytics: {
      displayed: EVENT_NAMES.AI_TILE_GENERATING_WARNING_DISPLAYED,
      tapped: EVENT_NAMES.AI_TILE_GENERATING_WARNING_TAPPED,
      dismissed: EVENT_NAMES.TILE_WARNING_DISMISSED,
    },
  },
}
